import { useTranslation } from "react-i18next";
import { OAuthError, postData } from "../../backend";
import { useModal } from "../../contexts";
import useUser from "../../hooks/useUser";
import ErrorModal from "../ErrorModal";
import OAuthModalHubspot from "../OAuthModalHubspot";
import { OnboardingDataSelection } from "./constants";
import OnboardingDealsCard from "./OnboardingDealsCard";

interface Props {
  selection: OnboardingDataSelection | null;
  onSelect: (selection: OnboardingDataSelection) => void;
}

function OnboardingData({ selection, onSelect }: Props) {
  const { t } = useTranslation();
  const { user, refreshUser } = useUser();
  const { openModal } = useModal();

  const hubspotCredentials = user?.hubspotCredentials || user?.activeTeam?.hubspotCredentials;

  async function connectHubspot(onConnected?: () => void) {
    const handleClose = async () => {
      await refreshUser();
      onConnected?.();
    };

    try {
      const { url } = await postData("/hubspot/install/");
      openModal(<OAuthModalHubspot oauthUrl={url} onClose={handleClose} />);
    } catch (e) {
      if (e instanceof OAuthError)
        openModal(<OAuthModalHubspot oauthUrl={e.oauthUrl} onClose={handleClose} />);
      else
        openModal(<ErrorModal details={e} />);
    }
  }

  return (
    <div className="flex flex-col gap-3 w-full">
      {hubspotCredentials ? (
        <OnboardingDealsCard
          portalId={hubspotCredentials.hubspotPortalId}
          selectedDealId={selection?.kind === "hubspot_deal" ? selection.dealId : undefined}
          onSelect={(dealId, dealName) => onSelect({ kind: "hubspot_deal", dealId, dealName })}
          onReconnect={connectHubspot}
        />
      ) : (
        <div className="flex items-center justify-between gap-3 border border-gray-200 rounded-lg px-4 py-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src={`${process.env.REACT_APP_STATIC_URL}/hubspot-icon.svg`} alt="" className="h-8 w-8 flex-shrink-0" />
            <div className="min-w-0">
              <div className="font-gilroy font-semibold text-gray-600">
                HubSpot <span className="text-gray-400">({t("Optional")})</span>
              </div>
              <div className="font-gilroy font-semibold text-gray-400 text-xs">{t("Not connected")}</div>
            </div>
          </div>
          <button type="button" className="btn btn-blue text-sm py-1 px-4 flex-shrink-0" onClick={() => connectHubspot()}>
            {t("Connect")}
          </button>
        </div>
      )}

      <button
        type="button"
        className={`w-full border rounded-lg px-4 py-3 text-left font-gilroy font-semibold text-sm text-gray-600 transition-colors ${selection?.kind === "sample" ? "border-blue bg-blue-50" : "border-gray-200 hover:border-blue"}`}
        onClick={() => onSelect({ kind: "sample" })}
      >
        {t("Use sample data")}
      </button>
    </div>
  );
}

export default OnboardingData;
